import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsQR from "jsqr";
import {
  Camera,
  RotateCcw,
  Check,
  ArrowLeft,
  Upload,
  QrCode,
  X,
  Loader2,
  ShoppingCart,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLicense } from "@/context/LicenseContext";

const CameraCapture = () => {
  const navigate = useNavigate();
  const { isReadOnly } = useLicense();

  // Video and stream refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanCanvasRef = useRef(null);
  const scanLoopRef = useRef(null);
  const isScanningActiveRef = useRef(false);
  const barcodeDetectorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Component state
  const [photo, setPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraLoading, setCameraLoading] = useState(true);
  const [cameraError, setCameraError] = useState(false);
  const [scanMode, setScanMode] = useState(true);
  const [eprelLoading, setEprelLoading] = useState(false);
  const [duplicateTire, setDuplicateTire] = useState(null);
  const [pendingEprelData, setPendingEprelData] = useState(null);
  const [showSellDialog, setShowSellDialog] = useState(false);
  const [soldQuantity, setSoldQuantity] = useState(1);

  // Zoom state: default 1x, presets: 0.5x, 1x, 2x
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hardwareZoomSupported, setHardwareZoomSupported] = useState(false);

  // Torch state
  const [torchOn, setTorchOn] = useState(false);

  // Initialize BarcodeDetector if natively supported by browser
  useEffect(() => {
    if (typeof window !== "undefined" && "BarcodeDetector" in window) {
      try {
        barcodeDetectorRef.current = new window.BarcodeDetector({
          formats: ["qr_code"],
        });
      } catch (e) {
        barcodeDetectorRef.current = null;
      }
    }
  }, []);

  // Stop camera tracks cleanly
  const stopCamera = useCallback(() => {
    isScanningActiveRef.current = false;
    if (scanLoopRef.current) {
      clearTimeout(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.warn("Error stopping track:", e);
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setTorchOn(false);
  }, []);

  // Start unified camera stream
  const startCamera = useCallback(
    async (facing = facingMode, customDeviceId = null) => {
      stopCamera();
      setCameraLoading(true);
      setCameraError(false);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraLoading(false);
        setCameraError(true);
        toast.error("La fotocamera non è supportata su questo browser.");
        return;
      }

      try {
        let stream = null;
        const videoConstraints = customDeviceId
          ? { deviceId: { exact: customDeviceId } }
          : {
              facingMode: { ideal: facing },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            };

        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: false,
          });
        } catch (err1) {
          console.warn("Fallback to basic camera constraints:", err1);
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: facing },
            audio: false,
          });
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch((e) => {
            console.warn("Video play error:", e);
          });
        }

        const track = stream.getVideoTracks()[0];
        if (track) {
          const capabilities = track.getCapabilities ? track.getCapabilities() : {};
          if (capabilities.zoom) {
            setHardwareZoomSupported(true);
          } else {
            setHardwareZoomSupported(false);
          }
        }

        setCameraLoading(false);
        setZoomLevel(1);
      } catch (err) {
        console.error("Camera access error:", err);
        setCameraLoading(false);
        setCameraError(true);
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          toast.error(
            "Permesso fotocamera negato. Abilita la fotocamera nelle impostazioni del browser."
          );
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          toast.error("Nessuna fotocamera trovata sul dispositivo.");
        } else {
          toast.error("Impossibile accedere alla fotocamera.");
        }
      }
    },
    [facingMode, stopCamera]
  );

  // Apply zoom (presets: 0.5x, 1x, 2x)
  const applyZoom = useCallback(
    async (level) => {
      setZoomLevel(level);
      const track = streamRef.current?.getVideoTracks()[0];
      if (!track) return;

      const caps = track.getCapabilities ? track.getCapabilities() : {};

      if (level === 0.5) {
        // 1. Try hardware zoom <= 0.5 if supported
        if (caps.zoom && caps.zoom.min <= 0.5) {
          try {
            await track.applyConstraints({
              advanced: [{ zoom: 0.5 }],
            });
            return;
          } catch (e) {
            console.warn("Hardware 0.5x zoom error:", e);
          }
        }

        // 2. Try switching to ultra-wide back camera if exposed
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoInputs = devices.filter((d) => d.kind === "videoinput");
          const ultraWide = videoInputs.find(
            (d) =>
              d.label.toLowerCase().includes("ultra") ||
              d.label.toLowerCase().includes("0.5") ||
              d.label.toLowerCase().includes("wide-angle") ||
              d.label.toLowerCase().includes("grandangolo")
          );
          const currentDeviceId = track.getSettings?.()?.deviceId;
          if (ultraWide && ultraWide.deviceId !== currentDeviceId) {
            await startCamera(facingMode, ultraWide.deviceId);
            return;
          }
        } catch (e) {}

        // Fallback: notify that physical 0.5x optics is unavailable
        toast.info("Ottica ultra-grandangolare 0.5x non rilevata su questo dispositivo.");
        return;
      }

      if (level === 1) {
        if (caps.zoom) {
          try {
            await track.applyConstraints({
              advanced: [{ zoom: 1 }],
            });
          } catch (e) {}
        }
        return;
      }

      if (level === 2) {
        if (caps.zoom && caps.zoom.max >= 2) {
          try {
            await track.applyConstraints({
              advanced: [{ zoom: 2 }],
            });
            return;
          } catch (e) {
            console.warn("Hardware 2x zoom error, using digital fallback:", e);
          }
        }
      }
    },
    [facingMode, startCamera]
  );

  // Toggle torch / flash with user feedback
  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) {
      toast.info("Fotocamera non attiva.");
      return;
    }

    try {
      const nextTorch = !torchOn;
      await track.applyConstraints({
        advanced: [{ torch: nextTorch }],
      });
      setTorchOn(nextTorch);
      toast.success(nextTorch ? "Flash / Torcia attiva" : "Flash / Torcia disattivata");
    } catch (e) {
      console.warn("Torch apply error:", e);
      toast.info("Flash/Torcia non supportata dal sensore o dal browser corrente.");
    }
  }, [torchOn]);

  // Flip camera between environment and user
  const toggleCamera = () => {
    const nextMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(nextMode);
    startCamera(nextMode);
  };

  // Capture photo from video stream (with digital crop if digital zoom is active)
  const captureFromVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return null;
    const canvas = document.createElement("canvas");
    const vw = video.videoWidth || video.clientWidth || 1280;
    const vh = video.videoHeight || video.clientHeight || 720;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    if (!hardwareZoomSupported && zoomLevel > 1) {
      // Digital crop matching the 2x zoomed view
      const cropW = vw / zoomLevel;
      const cropH = vh / zoomLevel;
      const startX = (vw - cropW) / 2;
      const startY = (vh - cropH) / 2;
      canvas.width = cropW;
      canvas.height = cropH;
      ctx.drawImage(video, startX, startY, cropW, cropH, 0, 0, cropW, cropH);
    } else {
      canvas.width = vw;
      canvas.height = vh;
      ctx.drawImage(video, 0, 0, vw, vh);
    }

    return canvas.toDataURL("image/jpeg", 0.85);
  }, [hardwareZoomSupported, zoomLevel]);

  // Handle detected QR code
  const handleDecodedQR = useCallback(
    async (decodedText) => {
      isScanningActiveRef.current = false;
      if (scanLoopRef.current) {
        clearTimeout(scanLoopRef.current);
        scanLoopRef.current = null;
      }

      const tyreTrackMatch = decodedText.match(
        /preventivofacilegommisti\.it\/t[iy]re\/([A-Z0-9]+)/i
      );

      if (tyreTrackMatch) {
        const tyreCode = tyreTrackMatch[1];
        stopCamera();
        toast.success(`Pneumatico trovato: ${tyreCode}`);
        navigate(`/tyre/${tyreCode}`);
        return;
      } else if (
        decodedText.includes("eprel.ec.europa.eu") ||
        decodedText.includes("/tyres/")
      ) {
        console.log("EPREL QR detected:", decodedText);
        const scanPhoto = captureFromVideo();
        stopCamera();
        await handleEPRELScan(decodedText, scanPhoto);
      } else {
        console.log("QR rilevato generico:", decodedText);
        toast.info(`QR Code rilevato: ${decodedText.substring(0, 50)}...`);
        setTimeout(() => {
          if (scanMode && !photo) {
            isScanningActiveRef.current = true;
            scanFrame();
          }
        }, 2000);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [captureFromVideo, navigate, photo, scanMode, stopCamera]
  );

  // Scan frame loop for QR code
  const scanFrame = useCallback(async () => {
    if (!isScanningActiveRef.current) return;

    const video = videoRef.current;
    if (!video || video.readyState < 2 || video.paused || video.ended) {
      if (isScanningActiveRef.current) {
        scanLoopRef.current = setTimeout(scanFrame, 150);
      }
      return;
    }

    let detectedText = null;

    // 1. Try native BarcodeDetector API if available
    if (barcodeDetectorRef.current) {
      try {
        const barcodes = await barcodeDetectorRef.current.detect(video);
        if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
          detectedText = barcodes[0].rawValue;
        }
      } catch (e) {}
    }

    // 2. Fallback to jsQR via offscreen canvas
    if (!detectedText) {
      try {
        let canvas = scanCanvasRef.current;
        if (!canvas) {
          canvas = document.createElement("canvas");
          scanCanvasRef.current = canvas;
        }

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw && vh) {
          const scale = Math.min(1, 640 / Math.max(vw, vh));
          const sw = Math.floor(vw * scale);
          const sh = Math.floor(vh * scale);

          if (canvas.width !== sw || canvas.height !== sh) {
            canvas.width = sw;
            canvas.height = sh;
          }

          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (ctx) {
            ctx.drawImage(video, 0, 0, sw, sh);
            const imageData = ctx.getImageData(0, 0, sw, sh);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: "dontInvert",
            });
            if (code && code.data) {
              detectedText = code.data;
            }
          }
        }
      } catch (e) {
        console.error("jsQR scan error:", e);
      }
    }

    if (detectedText && isScanningActiveRef.current) {
      handleDecodedQR(detectedText);
      return;
    }

    if (isScanningActiveRef.current) {
      scanLoopRef.current = setTimeout(scanFrame, 100);
    }
  }, [handleDecodedQR]);

  // Start camera on mount
  useEffect(() => {
    startCamera(facingMode);
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manage scanning loop when scanMode changes
  useEffect(() => {
    if (scanMode && !photo && !cameraLoading && !cameraError) {
      isScanningActiveRef.current = true;
      scanFrame();
    } else {
      isScanningActiveRef.current = false;
      if (scanLoopRef.current) {
        clearTimeout(scanLoopRef.current);
        scanLoopRef.current = null;
      }
    }
  }, [scanMode, photo, cameraLoading, cameraError, scanFrame]);

  // Handle EPREL QR Scan processing
  const handleEPRELScan = async (url, scanPhoto = null) => {
    setEprelLoading(true);
    try {
      console.log("EPREL URL:", url);
      const productIdMatch = url.match(/\/(?:tyres|qr)\/(\d+)/);
      if (!productIdMatch) {
        throw new Error("Impossibile estrarre l'ID prodotto dal QR code");
      }

      const productId = productIdMatch[1];
      const response = await api.get(`/eprel/product/${productId}`, {
        skipAuth: true,
      });
      const eprelData = response.data;

      const hasData =
        eprelData.tyreDesignation ||
        eprelData.additionalDetails?.commercialName ||
        eprelData.tyreSection ||
        eprelData.aspectRatio ||
        eprelData.rimDiameter;

      if (hasData) {
        toast.success("Dati EPREL importati!");
      } else {
        toast.info("QR EPREL rilevato - completa i dati manualmente");
      }

      if (hasData) {
        try {
          const fourSeasonKeywords = [
            "4s",
            "4seasons",
            "4 seasons",
            "all season",
            "4-season",
            "4 season",
            "4stagioni",
            "4 stagioni",
            "4-stagioni",
          ];
          const _nomeProdotto = eprelData?.additionalDetails?.commercialName
            ? eprelData.additionalDetails.commercialName
            : eprelData.tyreDesignation;
          const commercialNameLower = (_nomeProdotto || "").toLowerCase();
          const isFourSeason =
            eprelData.severeSnowTyre &&
            fourSeasonKeywords.some((k) => commercialNameLower.includes(k));
          const stagione = isFourSeason
            ? "4 Stagioni"
            : eprelData.severeSnowTyre
            ? "Invernale"
            : "Estivo";

          const checkData = {
            misura: `${eprelData.tyreSection ?? ""}${eprelData.aspectRatio ?? ""}${
              eprelData.rimDiameter ?? ""
            }`,
            marca: eprelData.supplierOrTrademark || "",
            nome_prodotto: _nomeProdotto || "",
            classe_energetica: eprelData.energyClass || "",
            codice_carico:
              eprelData.loadCapacityIndex != null
                ? String(eprelData.loadCapacityIndex)
                : "",
            codice_velocita: eprelData.speedCategorySymbol || "",
            stato: "Nuovo",
            stagione,
            eprel_code: parseInt(productId, 10),
          };

          const checkRes = await api.post("/tires/check-exists", checkData);

          setPendingEprelData({
            eprelData,
            scanPhoto,
            eprelUrl:
              eprelData.raw_data?.manual_url ||
              eprelData.raw_data?.eprel_url ||
              url,
            eprelCode: parseInt(productId, 10),
          });
          setDuplicateTire(checkRes.data);
          setEprelLoading(false);
          return;
        } catch (err) {
          if (err.response?.status === 404) {
            console.log("Tire not found, proceeding to form");
          } else {
            console.error("Check-exists error:", err);
          }
        }
      }

      navigate("/form", {
        state: {
          eprelData,
          fromEPREL: true,
          photo: scanPhoto,
          eprelUrl:
            eprelData.raw_data?.manual_url ||
            eprelData.raw_data?.eprel_url ||
            url,
          eprelCode: parseInt(productId, 10),
        },
      });
    } catch (error) {
      console.error("EPREL parsing error:", error);
      toast.info("QR rilevato - inserisci i dati manualmente");
      navigate("/form", {
        state: {
          eprelData: { raw_data: { eprel_url: url } },
          fromEPREL: true,
          photo: scanPhoto,
          eprelUrl: url,
          eprelCode: parseInt(productIdMatch?.[1] || "0", 10),
        },
      });
    } finally {
      setEprelLoading(false);
    }
  };

  // Photo mode actions
  const capture = useCallback(() => {
    const imageSrc = captureFromVideo();
    if (imageSrc) {
      setPhoto(imageSrc);
    }
  }, [captureFromVideo]);

  const retake = () => {
    setPhoto(null);
  };

  const confirmPhoto = () => {
    if (photo) {
      stopCamera();
      navigate("/form", { state: { photo } });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Per favore seleziona un file immagine");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const resultUrl = event.target?.result;
        if (scanMode) {
          const img = new Image();
          img.onload = async () => {
            let detectedText = null;
            if (barcodeDetectorRef.current) {
              try {
                const barcodes = await barcodeDetectorRef.current.detect(img);
                if (barcodes?.length > 0 && barcodes[0].rawValue) {
                  detectedText = barcodes[0].rawValue;
                }
              } catch (err) {}
            }
            if (!detectedText) {
              const canvas = document.createElement("canvas");
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(img, 0, 0);
                const imgData = ctx.getImageData(0, 0, img.width, img.height);
                const code = jsQR(imgData.data, imgData.width, imgData.height);
                if (code?.data) {
                  detectedText = code.data;
                }
              }
            }

            if (detectedText) {
              stopCamera();
              handleDecodedQR(detectedText);
            } else {
              toast.error("Nessun codice QR rilevato nell'immagine caricata.");
            }
          };
          img.src = resultUrl;
        } else {
          setPhoto(resultUrl);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSell = async () => {
    if (!duplicateTire) return;
    if (isReadOnly) {
      toast.error("Licenza scaduta. Rinnova per vendere pneumatici.");
      return;
    }

    try {
      const response = await api.post(`/tires/${duplicateTire.code}/sell`, {
        sold_date: new Date().toISOString(),
        numero_pneumatici: soldQuantity,
      });

      if (response.data.partial_sale) {
        toast.success(
          `Vendute ${response.data.quantity_sold} gomme su ${
            response.data.quantity_sold + response.data.quantity_remaining
          }. Nuovo codice gomme vendute: ${response.data.sold_code}`
        );
      } else {
        toast.success("Pneumatico segnato come venduto");
      }

      setShowSellDialog(false);
      setDuplicateTire(null);
      setPendingEprelData(null);
      stopCamera();
      navigate("/");
    } catch (error) {
      toast.error("Errore nella vendita");
    }
  };

  if (eprelLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Loader2 className="animate-spin text-primary" size={48} />
        <p className="text-muted-foreground">Caricamento dati EPREL...</p>
      </div>
    );
  }

  return (
    <div data-testid="camera-capture" className="space-y-6 w-full max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => {
            stopCamera();
            navigate("/");
          }}
          className="h-12 w-12 p-0"
        >
          <ArrowLeft size={24} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {scanMode ? "Scansiona QR EPREL" : "Scatta Foto"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {scanMode
              ? "Inquadra il QR code dell'etichetta EPREL"
              : "Fotografa il pneumatico o scansiona QR EPREL"}
          </p>
        </div>
      </div>

      {/* Duplicate tire modal */}
      {duplicateTire && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <Card className="w-full max-w-sm bg-card border-yellow-500/40 shadow-2xl">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <p className="font-semibold text-yellow-500 text-base">
                  Pneumatico già esistente
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDuplicateTire(null);
                    setPendingEprelData(null);
                    setShowSellDialog(false);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors ml-3 mt-0.5"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Esiste già un pneumatico con questi dati (codice:{" "}
                <span className="font-mono font-bold">{duplicateTire.code}</span>
                ). Vuoi inserirlo come nuovo record, venderlo o modificare quello
                esistente?
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-10"
                    onClick={() => {
                      if (pendingEprelData) {
                        stopCamera();
                        navigate("/form", {
                          state: {
                            eprelData: pendingEprelData.eprelData,
                            fromEPREL: true,
                            photo: pendingEprelData.scanPhoto,
                            eprelUrl: pendingEprelData.eprelUrl,
                            eprelCode: pendingEprelData.eprelCode,
                          },
                        });
                      }
                      setDuplicateTire(null);
                      setPendingEprelData(null);
                    }}
                    disabled={isReadOnly}
                  >
                    Inserisci nuovo
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 h-10 bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => {
                      stopCamera();
                      navigate(`/tyre/${duplicateTire.code}`, {
                        state: { editMode: true },
                      });
                      setDuplicateTire(null);
                      setPendingEprelData(null);
                    }}
                  >
                    Modifica esistente
                  </Button>
                </div>
                <Button
                  type="button"
                  className="w-full h-10 bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => {
                    setSoldQuantity(duplicateTire.numero_pneumatici);
                    setShowSellDialog(true);
                  }}
                  disabled={isReadOnly}
                >
                  <ShoppingCart size={16} className="mr-2" />
                  Vendi
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sell Dialog */}
      <AlertDialog open={showSellDialog} onOpenChange={setShowSellDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Segna come venduto</AlertDialogTitle>
            <AlertDialogDescription>
              Specifica quante gomme sono state vendute.
              {duplicateTire && soldQuantity < duplicateTire.numero_pneumatici
                ? " Le gomme rimanenti resteranno in magazzino."
                : " Questo pneumatico verrà spostato nell'archivio venduti."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label
              htmlFor="sold-quantity-modal"
              className="text-sm font-medium mb-2 block"
            >
              Quantità venduta
            </Label>
            <Input
              id="sold-quantity-modal"
              type="number"
              min="1"
              max={duplicateTire?.numero_pneumatici || 1}
              value={soldQuantity}
              onChange={(e) => setSoldQuantity(parseInt(e.target.value))}
              className="w-full"
              data-testid="sold-quantity-input-modal"
            />
            {duplicateTire && (
              <p className="text-sm text-muted-foreground mt-2">
                Vendi {soldQuantity} di {duplicateTire.numero_pneumatici} gomme
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowSellDialog(false);
                setSoldQuantity(duplicateTire?.numero_pneumatici || 1);
              }}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-sell-btn-modal"
              onClick={handleSell}
              className="bg-blue-600 text-white hover:bg-blue-700"
              disabled={
                isReadOnly ||
                !duplicateTire ||
                !soldQuantity ||
                soldQuantity < 1 ||
                soldQuantity > (duplicateTire?.numero_pneumatici || 0)
              }
            >
              Conferma Vendita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mode Toggle (Foto / Scansiona EPREL) */}
      <div className="flex gap-2 w-full">
        <Button
          data-testid="photo-mode-btn"
          variant={!scanMode ? "default" : "secondary"}
          onClick={() => setScanMode(false)}
          className={`flex-1 h-12 ${
            !scanMode ? "bg-primary text-primary-foreground" : ""
          }`}
        >
          <Camera size={18} className="mr-2" />
          Foto
        </Button>
        <Button
          data-testid="qr-mode-btn"
          variant={scanMode ? "default" : "secondary"}
          onClick={() => {
            setPhoto(null);
            setScanMode(true);
          }}
          className={`flex-1 h-12 ${
            scanMode ? "bg-primary text-primary-foreground" : ""
          }`}
        >
          <QrCode size={18} className="mr-2" />
          Scansiona EPREL
        </Button>
      </div>

      {/* Viewfinder Card: perfectly centered with w-full and no forced min-height */}
      <Card className="bg-card border-border overflow-hidden w-full max-w-full">
        <CardContent className="p-0">
          {photo ? (
            <div className="relative w-full aspect-[4/3] sm:aspect-video overflow-hidden">
              <img
                data-testid="photo-preview"
                src={photo}
                alt="Anteprima foto"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          ) : cameraError ? (
            <div className="aspect-[4/3] sm:aspect-video bg-secondary flex flex-col items-center justify-center p-6 text-center">
              <Camera size={44} className="text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm mb-4">
                Fotocamera non disponibile
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => startCamera(facingMode)}
                >
                  <RotateCcw size={16} className="mr-2" />
                  Riprova
                </Button>
                <Button
                  data-testid="upload-fallback-btn"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-primary text-primary-foreground"
                >
                  <Upload size={16} className="mr-2" />
                  Carica Immagine
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative bg-black flex items-center justify-center overflow-hidden w-full max-w-full aspect-[4/3] sm:aspect-video">
              {/* Native HTML5 Video Element */}
              <video
                ref={videoRef}
                playsInline
                autoPlay
                muted
                style={{
                  transform:
                    !hardwareZoomSupported && zoomLevel > 1
                      ? `scale(${zoomLevel})`
                      : undefined,
                  transformOrigin: "center center",
                  transition: "transform 0.2s ease-out",
                }}
                className="w-full h-full object-cover"
              />

              {/* Loading Spinner */}
              {cameraLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <div className="text-center">
                    <Loader2
                      className="animate-spin text-primary mx-auto mb-2"
                      size={36}
                    />
                    <p className="text-white text-sm">Avvio fotocamera...</p>
                  </div>
                </div>
              )}

              {/* QR Scan Visual Guide Overlay */}
              {!cameraLoading && scanMode && (
                <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center p-4">
                  <div className="w-48 h-48 sm:w-60 sm:h-60 border-2 border-primary/90 rounded-2xl relative shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]">
                    <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 border-primary rounded-tl-xl -mt-1 -ml-1" />
                    <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 border-primary rounded-tr-xl -mt-1 -mr-1" />
                    <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 border-primary rounded-bl-xl -mb-1 -ml-1" />
                    <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 border-primary rounded-br-xl -mb-1 -mr-1" />
                    <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-0.5 bg-primary/60 animate-pulse shadow-[0_0_12px_#3b82f6]" />
                  </div>
                  {/* <span className="mt-3 bg-black/75 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                    Inquadra il QR code EPREL
                  </span> */}
                </div>
              )}

              {/* Photo Framing Guide Overlay */}
              {!cameraLoading && !scanMode && (
                <div className="absolute inset-4 sm:inset-8 border border-white/25 rounded-xl pointer-events-none" />
              )}

              {/* Top Controls: Torch & Camera Switch (ALWAYS visible on live camera) */}
              {!cameraLoading && (
                <div className="absolute top-3 right-3 flex items-center gap-2 z-20">
                  <button
                    type="button"
                    onClick={toggleTorch}
                    className={`p-2.5 rounded-full backdrop-blur-md transition-all shadow-md ${
                      torchOn
                        ? "bg-yellow-400 text-black shadow-yellow-400/30"
                        : "bg-black/60 text-white/90 hover:bg-black/80 border border-white/10"
                    }`}
                    title={torchOn ? "Spegni Torcia" : "Accendi Torcia"}
                    aria-label="Torcia"
                  >
                    <Zap size={18} className={torchOn ? "fill-current text-black" : "text-white/90"} />
                  </button>
                  <button
                    type="button"
                    onClick={toggleCamera}
                    className="p-2.5 rounded-full bg-black/60 hover:bg-black/80 text-white/90 border border-white/10 backdrop-blur-md transition-all shadow-md"
                    title="Cambia fotocamera"
                    aria-label="Cambia fotocamera"
                  >
                    <RotateCcw size={18} />
                  </button>
                </div>
              )}

              {/* Bottom Zoom Controls Overlay: 0.5x, 1x, 2x */}
              {!cameraLoading && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/65 backdrop-blur-md px-2.5 py-1.5 rounded-full border border-white/15 z-20 shadow-lg">
                  {[0.5, 1, 2].map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => applyZoom(lvl)}
                      className={`w-9 h-8 rounded-full text-xs font-bold transition-all ${
                        zoomLevel === lvl
                          ? "bg-primary text-primary-foreground scale-110 shadow-md ring-2 ring-primary/40"
                          : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                    >
                      {lvl === 0.5 ? "0.5x" : `${lvl}x`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Controls */}
      {!scanMode && (
        <div className="w-full">
          {photo ? (
            <div className="flex justify-center items-center gap-4">
              <Button
                data-testid="retake-btn"
                variant="secondary"
                onClick={retake}
                className="h-12 px-6 font-semibold"
              >
                <RotateCcw size={18} className="mr-2" />
                Rifai
              </Button>
              <Button
                data-testid="confirm-photo-btn"
                onClick={confirmPhoto}
                className="h-12 px-8 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              >
                <Check size={18} className="mr-2" />
                Conferma
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-3 items-center w-full max-w-sm mx-auto">
              <div className="flex justify-start">
                {/* Spacer for balance */}
              </div>
              <div className="flex justify-center">
                {!cameraError && (
                  <Button
                    data-testid="capture-btn"
                    onClick={capture}
                    className="h-16 w-16 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow p-0 shadow-xl active-scale"
                    aria-label="Scatta foto"
                  >
                    <Camera size={30} />
                  </Button>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  data-testid="upload-btn"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-12 w-12 p-0 rounded-full shadow-md"
                  title="Carica da galleria"
                  aria-label="Carica da galleria"
                >
                  <Upload size={20} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {scanMode && (
        <div className="flex justify-center">
          <Button
            data-testid="upload-qr-btn"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            className="h-12 px-5"
          >
            <Upload size={18} className="mr-2" />
            Carica Immagine QR
          </Button>
        </div>
      )}

      {/* Hidden file input for gallery upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
        data-testid="file-input"
      />

      {/* Helpful tips */}
      {!photo && !cameraError && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2 text-sm uppercase tracking-wider text-muted-foreground">
              Suggerimenti
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>
                • <strong>Zoom:</strong> Usa <strong>0.5x</strong> (grandangolo),{" "}
                <strong>1x</strong> o <strong>2x</strong> per regolare l'inquadratura
              </li>
              <li>
                • <strong>Torcia:</strong> Tocca l'icona del fulmine in alto a destra per accendere la luce
              </li>
              <li>
                • <strong>Scansione EPREL:</strong> Inquadra il QR code adesivo per compilare automaticamente la scheda
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CameraCapture;
